import { calculateSubscriptionRecurringSubtotal } from '../billing-and-plans/billing-and-plans-calculation';
import { LocationRegisterList } from 'src/app/_data/location-resgister';

function toNumber(value: any): number {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
}

// Per-unit catalog price for each resource — used to sanity-check subscriptionPlanPkg rows below.
const REGISTER_UNIT_PRICE = LocationRegisterList.find((i) => i.id === 1)?.price ?? 49;
const LOCATION_UNIT_PRICE = LocationRegisterList.find((i) => i.id === 2)?.price ?? 79;

export function buildRecurringSubscriptionTotals(subtotal: number, taxRate: number) {
    const subTotalCost = +toNumber(subtotal).toFixed(2);
    const tax = taxRate ? +((subTotalCost * taxRate) / 100).toFixed(2) : 0;
    return {
        subTotalCost,
        TotalTaxAmount: tax.toFixed(2),
        totalCost: (subTotalCost + tax).toFixed(2),
    };
}

export function buildUsageReceiptTotals(lines: any[] = []) {
    const subtotal = +lines.reduce((sum, line) => {
        const lineSubtotal = toNumber(line?.subTotalCost ?? line?.SubTotalCost);
        if (lineSubtotal > 0) return sum + lineSubtotal;
        return sum + toNumber(line?.totalPrice ?? line?.TotalPrice);
    }, 0).toFixed(2);

    const tax = +lines.reduce((sum, line) => sum + toNumber(line?.taxAmount ?? line?.TaxAmount), 0).toFixed(2);
    const total = +lines.reduce((sum, line) => {
        const lineTotal = toNumber(line?.totalPrice ?? line?.TotalPrice);
        if (lineTotal > 0) return sum + lineTotal;
        return sum + toNumber(line?.subTotalCost ?? line?.SubTotalCost) + toNumber(line?.taxAmount ?? line?.TaxAmount);
    }, 0).toFixed(2);

    return { subtotal, tax, total };
}

export function readInvoiceRecurringSubtotal(invoice: any): number {
    const subtotal = toNumber(invoice?.invoiceSubtotal ?? invoice?.InvoiceSubtotal);
    if (subtotal > 0) return +subtotal.toFixed(2);

    const details: any[] = invoice?.packageDetails ?? invoice?.PackageDetails ?? [];
    return +details.reduce((sum, line) => {
        const amount = toNumber(line?.subTotalCost ?? line?.SubTotalCost);
        if (amount > 0) return sum + amount;
        return sum + toNumber(line?.totalPrice ?? line?.TotalPrice);
    }, 0).toFixed(2);
}

export function readInvoiceRecurringExtraQuantity(invoice: any, resourceId: 1 | 2): number {
    const details: any[] = invoice?.packageDetails ?? invoice?.PackageDetails ?? [];
    const needle = resourceId === 1 ? 'register' : 'location';
    const line = details.find((item) => String(item?.title ?? item?.Title ?? '').trim().toLowerCase().includes(needle));
    return toNumber(line?.quantity ?? line?.Quantity ?? line?.qty ?? line?.Qty);
}

export function readSubscriptionRecurringSubtotal(subscription: any, taxRate: number, addons: any[] = []): number {
    // Prefer the freshly-computed total over the stored subTotalCost/SubTotalCost field, not
    // Math.max() of the two: the stored field can go stale (confirmed on a real account — it read
    // $1,613 while the actual subscribedFeatures line items, and Stripe's real invoice, both said
    // $1,485), and Math.max() always picks the larger number regardless of which one is actually
    // right, silently inflating the Order Summary whenever the stale figure happens to be bigger.
    // billing-and-plans.component.ts's nextPaymentAmount already prefers live sources (the current
    // invoice, then a fresh recompute) over this same stored field for the same reason.
    const activeItemsSubtotal = calculateSubscriptionRecurringSubtotal(subscription, addons);
    if (activeItemsSubtotal > 0) {
        return +activeItemsSubtotal.toFixed(2);
    }

    const subtotal = toNumber(subscription?.subTotalCost ?? subscription?.SubTotalCost);
    if (subtotal > 0) {
        return +subtotal.toFixed(2);
    }

    const total = toNumber(subscription?.totalCost ?? subscription?.TotalCost);
    if (!total) return 0;

    const rate = toNumber(subscription?.totalTaxValue ?? subscription?.TotalTaxValue ?? taxRate);
    if (!rate) return +total.toFixed(2);

    return +(total / (1 + rate / 100)).toFixed(2);
}

/** A row only counts as a real Registers/Locations line if its per-unit price actually matches that
 *  resource's catalog price (monthly or yearly). Guards against a persisted subscriptionPlanPkg row
 *  whose packageId happens to be "1"/"2" for an unrelated reason (confirmed on a real account: the
 *  plan's own $35 line was stored under packageId "2" — see resolvePlan()'s planId-fallback doc
 *  comment in payment-page.component.ts — which this packageId-only match then misread as "1 extra
 *  location purchased"). packageId alone isn't a safe identifier since it collides with the local
 *  plan catalog's small per-plan ids (1=Free, 2=Plus, ...). */
function isPlausibleUnitPrice(item: any, unitPrice: number): boolean {
    const qty = toNumber(item?.qty ?? item?.Qty) || 1;
    // Prefer pricePerPackage; some older/synthesized rows only carry subTotalCost, so derive the
    // effective per-unit price from that instead of treating a missing pricePerPackage as "unknown
    // — trust it" (which would let the exact bug this guards against back in for such rows).
    const pricePerPackage = toNumber(item?.pricePerPackage ?? item?.PricePerPackage);
    const price = pricePerPackage > 0 ? pricePerPackage : toNumber(item?.subTotalCost ?? item?.SubTotalCost) / qty;
    if (price <= 0) return false;
    return Math.abs(price - unitPrice) < 0.5 || Math.abs(price - unitPrice * 12) < 0.5;
}

export function readSubscriptionRecurringExtraQuantity(subscription: any, resourceId: 1 | 2): number {
    const packageId = resourceId === 1 ? '1' : '2';
    const unitPrice = resourceId === 1 ? REGISTER_UNIT_PRICE : LOCATION_UNIT_PRICE;
    const rows: any[] = subscription?.subscriptionPlanPkg ?? subscription?.SubscriptionPlanPkg ?? [];
    const line = rows.find((item) =>
        String(item?.packageId ?? item?.PackageId ?? '') === packageId
        && isPlausibleUnitPrice(item, unitPrice)
    );
    return toNumber(line?.qty ?? line?.Qty);
}

export function readOwnedRecurringQuantity(
    setup: any,
    invoice: any,
    resourceId: 1 | 2,
    defaultQty: number,
): number {
    const includedQty = Math.max(0, toNumber(defaultQty));
    const subscription = setup?.subscription ?? setup?.Subscription ?? {};
    const invoiceOwnedQty = includedQty + readInvoiceRecurringExtraQuantity(invoice, resourceId);
    const packageOwnedQty = includedQty + readSubscriptionRecurringExtraQuantity(subscription, resourceId);
    const accountTotal = resourceId === 1
        ? toNumber(setup?.perNoOfRegister ?? setup?.PerNoOfRegister ?? setup?.noOfRegisters ?? setup?.NoOfRegisters)
        : toNumber(setup?.noOfLocations ?? setup?.NoOfLocations);
    const features: any[] = subscription?.subscribedFeatures ?? subscription?.SubscribedFeatures ?? [];
    const hasTypedEntries = features.some((feature: any) => (feature?.type ?? feature?.Type) != null);

    let featureOwnedQty = includedQty;
    if (hasTypedEntries) {
        const featureType = resourceId === 1 ? 6 : 7;
        const billedExtra = features.reduce((sum: number, feature: any) => {
            const active = feature?.isActive ?? feature?.IsActive;
            const type = toNumber(feature?.type ?? feature?.Type);
            return active && type === featureType ? sum + toNumber(feature?.qty ?? feature?.Qty) : sum;
        }, 0);
        featureOwnedQty += billedExtra;
    } else {
        const titleNeedle = resourceId === 1 ? 'regist' : 'location';
        const billedExtra = features.reduce((sum: number, feature: any) => {
            const title = String(feature?.title ?? feature?.Title ?? '').trim().toLowerCase();
            const active = feature?.isActive ?? feature?.IsActive;
            const addon = feature?.isAddon ?? feature?.IsAddon;
            return active && addon && title.includes(titleNeedle)
                ? sum + toNumber(feature?.qty ?? feature?.Qty)
                : sum;
        }, 0);
        featureOwnedQty += billedExtra;
    }

    return Math.max(includedQty, invoiceOwnedQty, packageOwnedQty, accountTotal, featureOwnedQty);
}

export function readAvailableBusinessSetup(userInfo: any, persistedSetup: any): any | null {
    const nestedSetup = userInfo?.businessSetup?.[0] ?? userInfo?.BusinessSetup?.[0];
    if (nestedSetup) return nestedSetup;

    if (Array.isArray(userInfo) && userInfo[0]) return userInfo[0];
    if (userInfo?.subscription ?? userInfo?.Subscription) return userInfo;

    if (typeof persistedSetup === 'string') {
        try {
            return JSON.parse(persistedSetup);
        } catch {
            return null;
        }
    }

    return persistedSetup || null;
}
